import { useState } from "react";
import { AnimatedText, AnimatedTitle } from "@/components/ui/AnimatedText";
import { MapPin, Phone, Mail, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSEO } from "@/utils/useSEO";

export default function Contact() {
  const { t } = useTranslation();
  useSEO(
    "Contact Us",
    "Get in touch with Patal Kikian Villas in Sidemen, Bali. Reach us by phone, email, or WhatsApp to book your stay, ask questions, or plan your perfect Bali highland retreat. We are always happy to assist."
  );
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("https://formsubmit.co/ajax/info@patalkikianbali.com", {
        method: "POST",
        headers: { 
            "Content-Type": "application/json",
            "Accept": "application/json"
        },
        body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            subject: formData.subject,
            message: formData.message,
            _subject: formData.subject || "New Message from Website",
            _template: "table"
        })
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: "", email: "", subject: "", message: "" });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        alert(t("contact.form.error", "Oops! Something went wrong. Please try again later."));
      }
    } catch (error) {
      console.error(error);
      alert(t("contact.form.error", "Oops! Something went wrong. Please try again later."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section className="relative min-h-[100dvh] w-full flex items-center justify-center">
        <div className="absolute inset-0">
          <img src="/media/patal-kikian-hero-banner.webp" alt="Contact Background" className="w-full h-full object-cover opacity-85" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
        </div>
        
        <div className="relative z-10 text-center px-4 mt-20">
          <AnimatedText delay={0.2} className="text-gold uppercase tracking-[0.3em] text-sm mb-4 font-semibold">
            {t("contact.hero.subtitle", "Get in touch")}
          </AnimatedText>
          <AnimatedTitle
            title={t("contact.hero.title", "Contact Us")}
            className="text-4xl md:text-5xl lg:text-6xl font-luxury text-shadow-lg leading-tight mb-6 max-w-4xl mx-auto"
          />
        
          <AnimatedText delay={0.3}>
            <p className="text-white/80 max-w-3xl mx-auto text-lg leading-relaxed">
              {t("contact.hero.desc", "We are here to assist you. Reach out to us for inquiries, reservations, or any special requests.")}
            </p>
          </AnimatedText>
          
        </div>
      
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20">
          <button 
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
            className="text-white/70 hover:text-white transition-colors duration-300 flex flex-col items-center gap-2 cursor-pointer"
            aria-label="Scroll to content"
          >
            <span className="uppercase tracking-[0.2em] text-[10px]">{t("contact.hero.scroll", "Get in Touch")}</span>
            <ChevronDown size={24} className="animate-bounce" />
          </button>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            
            <div className="flex flex-col gap-8">
              <AnimatedText delay={0.2}>
                <h2 className="text-3xl md:text-4xl font-luxury mb-4">{t("contact.info.title", "Information")}</h2>
                <p className="text-white/70 leading-relaxed">
                  {t("contact.info.desc", "Have a question or need assistance? Our team is always ready to help you plan your perfect stay in Sidemen.")}
                </p>
              </AnimatedText>

              <AnimatedText delay={0.3} className="space-y-6 mt-4">
                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 rounded-full border border-gold/30 flex items-center justify-center bg-white/5 text-gold group-hover:bg-gold group-hover:text-background transition-all duration-300 flex-shrink-0">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-1 uppercase tracking-widest text-sm text-gold">Location</h3>
                    <p className="text-white/70 leading-relaxed">
                      {t("contact.info.address", "Jl. Raya Sidemen, Sinduwati, Sidemen, Kabupaten Karangasem, Bali 80864")}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 rounded-full border border-gold/30 flex items-center justify-center bg-white/5 text-gold group-hover:bg-gold group-hover:text-background transition-all duration-300 flex-shrink-0">
                    <Phone size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-1 uppercase tracking-widest text-sm text-gold">Phone / WhatsApp</h3>
                    <a href="https://wa.me/6281353278504" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-gold transition-colors">
                      +62 813 5327 8504
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 rounded-full border border-gold/30 flex items-center justify-center bg-white/5 text-gold group-hover:bg-gold group-hover:text-background transition-all duration-300 flex-shrink-0">
                    <Mail size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-1 uppercase tracking-widest text-sm text-gold">Email</h3>
                    <a href="mailto:info@patalkikianbali.com" className="text-white/70 hover:text-gold transition-colors">
                      info@patalkikianbali.com
                    </a>
                  </div>
                </div>
              </AnimatedText>
            </div>

            <AnimatedText delay={0.4} className="bg-white/5 p-8 md:p-10 rounded-xl border border-white/10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold/5 rounded-bl-full -z-10" />
              <h2 className="text-2xl font-luxury mb-6">{t("contact.form.title", "Send us a Message")}</h2>
              
              {submitted ? (
                <div className="bg-green-500/10 border border-green-500/20 text-green-400 p-6 rounded-lg text-center">
                  <p>{t("contact.form.success", "Thank you! Your message has been sent successfully.")}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <input type="text" name="_honey" style={{ display: 'none' }} />
                  <input type="hidden" name="_captcha" value="false" />
                  
                  <div>
                    <label htmlFor="name" className="block text-sm text-white/60 mb-2 uppercase tracking-widest">{t("contact.form.name", "Your Name")}</label>
                    <input
                      type="text"
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full bg-background border border-white/20 rounded-sm px-4 py-3 focus:outline-none focus:border-gold transition-colors text-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm text-white/60 mb-2 uppercase tracking-widest">{t("contact.form.email", "Your Email")}</label>
                    <input
                      type="email"
                      id="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full bg-background border border-white/20 rounded-sm px-4 py-3 focus:outline-none focus:border-gold transition-colors text-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm text-white/60 mb-2 uppercase tracking-widest">{t("contact.form.subject", "Subject")}</label>
                    <input
                      type="text"
                      id="subject"
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      className="w-full bg-background border border-white/20 rounded-sm px-4 py-3 focus:outline-none focus:border-gold transition-colors text-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm text-white/60 mb-2 uppercase tracking-widest">{t("contact.form.message", "Message")}</label>
                    <textarea
                      id="message"
                      required
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full bg-background border border-white/20 rounded-sm px-4 py-3 focus:outline-none focus:border-gold transition-colors text-white resize-none"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gold text-background font-medium rounded-sm px-6 py-4 uppercase tracking-widest text-sm hover:bg-white transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? t("contact.form.btnSending", "Sending...") : t("contact.form.btnSend", "Send Message")}
                  </button>
                </form>
              )}
            </AnimatedText>

          </div>
        </div>
      </section>

      <section className="h-[500px] w-full grayscale-[20%] hover:grayscale-0 transition-all duration-1000">
        <iframe 
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15783.561918398189!2d115.43799639534604!3d-8.47959080801323!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2dd21c60d8ff04e9%3A0x868b4461db9abce7!2sPatal%20Kikian%20Villas!5e0!3m2!1sen!2sid!4v1709623821010!5m2!1sen!2sid" 
          width="100%" 
          height="100%" 
          style={{ border: 0 }} 
          allowFullScreen 
          loading="lazy" 
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </section>
    </>
  );
}
